import * as RadixAccordion from '@radix-ui/react-accordion'
import { useEffect, useCallback, useRef, type ReactNode } from 'react'
import type { ScreenInteractionContext } from '../platform/analytics/interactionAnalytics'
import { trackAccordionToggle } from '../platform/analytics/interactionAnalytics'
import { BuxusAccordionChevron } from './BuxusAccordionChevron'

export type BuxusAccordionItem = {
  id: string
  title: string
  description?: string
  content: ReactNode
}

type BuxusAccordionProps = {
  items: BuxusAccordionItem[]
  /** Controlled open panel ids. */
  expandedIds?: string[]
  onExpandedIdsChange?: (ids: string[]) => void
  /** When only one panel exists, mobile expands it by default (uncontrolled). */
  defaultExpandedIds?: string[]
  /** Mobile parity: screen / operator / asset on expand & collapse. */
  analytics?: ScreenInteractionContext
  className?: string
}

/**
 * Accessible accordion (Radix) styled like legacy mobile `List.Accordion` headers.
 */
export function BuxusAccordion({
  items,
  expandedIds,
  onExpandedIdsChange,
  defaultExpandedIds,
  analytics,
  className,
}: BuxusAccordionProps) {
  const expandedRef = useRef<string[]>(expandedIds ?? defaultExpandedIds ?? [])

  useEffect(() => {
    if (expandedIds) expandedRef.current = expandedIds
  }, [expandedIds])

  const emitAccordionAnalytics = useCallback(
    (prevIds: string[], nextIds: string[]) => {
      if (!analytics) return
      const prev = new Set(prevIds)
      const next = new Set(nextIds)
      for (const item of items) {
        const wasOpen = prev.has(item.id)
        const isOpen = next.has(item.id)
        if (wasOpen !== isOpen) {
          trackAccordionToggle(isOpen, item.title, analytics)
        }
      }
    },
    [analytics, items],
  )

  const handleValueChange = useCallback(
    (nextIds: string[]) => {
      emitAccordionAnalytics(expandedRef.current, nextIds)
      expandedRef.current = nextIds
      onExpandedIdsChange?.(nextIds)
    },
    [emitAccordionAnalytics, onExpandedIdsChange],
  )

  if (!items.length) return null

  const isControlled = expandedIds != null && onExpandedIdsChange != null

  return (
    <RadixAccordion.Root
      type="multiple"
      className={['buxus-accordion', className].filter(Boolean).join(' ')}
      {...(isControlled
        ? { value: expandedIds, onValueChange: handleValueChange }
        : { defaultValue: defaultExpandedIds, onValueChange: handleValueChange })}
    >
      {items.map((item) => (
        <RadixAccordion.Item key={item.id} value={item.id} className="buxus-accordion-item">
          <RadixAccordion.Header className="buxus-accordion-header">
            <RadixAccordion.Trigger className="buxus-accordion-trigger">
              <span className="buxus-accordion-trigger-text">
                <span className="buxus-accordion-title">{item.title}</span>
                {item.description ? (
                  <span className="buxus-accordion-description">{item.description}</span>
                ) : null}
              </span>
              <span className="buxus-accordion-chevron" aria-hidden>
                <BuxusAccordionChevron />
              </span>
            </RadixAccordion.Trigger>
          </RadixAccordion.Header>
          <RadixAccordion.Content className="buxus-accordion-content">
            <div className="buxus-accordion-content-inner">{item.content}</div>
          </RadixAccordion.Content>
        </RadixAccordion.Item>
      ))}
    </RadixAccordion.Root>
  )
}
